import {colors} from '../colors/colors';
import type {UsageFooterType} from '../sidebar-nav/types';

type NavItem = {
    requiresUpgrade: boolean;
    preventAutoSort?: boolean;
} & any;

// Sort nav items so that featureGated items are at the bottom
export function sortNavItems(navItems: NavItem[]): NavItem[] {
    return navItems.sort((a, b) => {
        if (a.preventAutoSort || b.preventAutoSort) {
            return 0;
        }

        if (a.requiresUpgrade && !b.requiresUpgrade) {
            return 1;
        } else if (!a.requiresUpgrade && b.requiresUpgrade) {
            return -1;
        } else {
            return 0;
        }
    });
}

export function getLimitString(type: UsageFooterType): string {
    switch (type) {
        case 'ai':
            return NutClientConfig.hasAiOutcomesFF ? 'outcome' : 'credit';
        case 'visitoriq':
        case 'smsCampaigns':
        case 'inbox':
            return 'credit';
        case 'campaigns':
            return 'email';
        case 'contextDocuments':
            return 'document';
        default:
            return '';
    }
}

export function getProgressBarColor(percentComplete: number, approachingLimitAmount: number) {
    let color;
    if (percentComplete >= approachingLimitAmount && percentComplete < 1) {
        color = colors.orange;
    } else if (percentComplete === 1) {
        color = colors.red;
    } else {
        color = colors.blue;
    }

    return color;
}

// Select color with full context of type; for contextDocuments, uses absolute usage/limit
export function getProgressBarColorByType(
    type: UsageFooterType,
    percentComplete: number,
    approachingLimitAmount: number,
    usage: number,
    limit: number
): string {
    if (type === 'contextDocuments') {
        return getContextDocumentsProgressColor(usage, limit);
    }

    return getProgressBarColor(percentComplete, approachingLimitAmount);
}

function getContextDocumentsProgressColor(wordsUsage: number, wordsLimit: number): string {
    const firstStepMax = 5000;
    const usagePercent = wordsLimit ? wordsUsage / wordsLimit : 0;

    if (wordsUsage < firstStepMax) {
        return colors.yellow;
    } else if (usagePercent < 0.9) {
        return colors.green;
    } else if (usagePercent < 1) {
        return colors.orange;
    } else {
        return colors.red;
    }
}

export function getUsageMessage(
    type: UsageFooterType,
    usage: number,
    limit: number,
    isFeatureGated?: boolean
): string {
    if (isFeatureGated) {
        switch (type) {
            case 'campaigns':
                return 'Upgrade to access Nutshell’s Email Marketing';
            case 'inbox':
                return 'Purchase Engagement Pro to use SMS engagement';
            case 'smsCampaigns':
                return 'Purchase Marketing Pro to use SMS campaigns';
            case 'visitoriq':
                return 'Purchase a Nutshell IQ plan to use Visitor IQ';
            case 'ai':
                return 'Upgrade your plan for more transcriptions and summaries';
        }
    }

    const limitName = getLimitString(type);
    const usageRemaining = limit - usage;

    if (type === 'campaigns') {
        if (usageRemaining <= 0) {
            return `You’ve used all ${limit.toLocaleString()} of your plan's contacts.`;
        }

        return `You can add ${limit - usage} more ${
            limit - usage === 1 ? 'contact' : 'contacts'
        } in your current tier.`;
    }

    if (type === 'contextDocuments') {
        if (usageRemaining <= 0) {
            return `Nutshell’s AI chatbot tool has a limit of collecting ${limit} words before it hits its knowledge capacity.`;
        }

        return `You have ${usageRemaining} words remaining before you hit your knowledge capacity.`;
    }

    if (usageRemaining <= 0) {
        return `You’ve used all ${limit.toLocaleString()} of your plan's ${limitName}s this month.`;
    }

    return `You can use ${usageRemaining.toLocaleString()} ${
        usageRemaining === 1 ? `${limitName}` : `${limitName}s`
    } out of the ${limit.toLocaleString()} available this month.`;
}

export function getUsageTitle(type: UsageFooterType): string {
    switch (type) {
        case 'visitoriq':
            return 'Nutshell IQ';
        case 'inbox':
        case 'smsCampaigns':
            return 'SMS credits';
        case 'campaigns':
            return 'Email marketing';
        case 'ai':
            return NutClientConfig.hasAiOutcomesFF ? 'AI outcomes' : 'AI credits';
        case 'contextDocuments':
            return 'Knowledge capacity';
        default:
            return '';
    }
}
